import { Columns2, MessageSquare, X } from "lucide-react";
import { useEffect, useRef } from "react";

import type { GtmBrainDoc } from "../api/GtmApi";
import { listPillars, pillarSections } from "./pillars";

type PillarFieldId = "pain" | "valueProposition" | "proof";

type Props = {
  docs: GtmBrainDoc[];
  pillarKey: string;
  onDocsChange: (docs: GtmBrainDoc[]) => void;
  onDiscard: () => void;
  onBackToPillars: () => void;
  onBackToOverview: () => void;
};

export default function BrainPillarDocView({
  docs,
  pillarKey,
  onDocsChange,
  onDiscard,
  onBackToPillars,
  onBackToOverview,
}: Props) {
  const pillars = listPillars(docs);
  const item = pillars.find((p) => p.key === pillarKey) || pillars[0];
  const sections = item ? pillarSections(item.doc) : [];
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [pillarKey]);

  if (!item) {
    return (
      <div className="flex h-full w-[min(100%,28rem)] shrink-0 min-w-0 items-center justify-center border-r border-slate-200 bg-white text-sm text-slate-500">
        Pillar not found.
      </div>
    );
  }

  const updateField = (field: PillarFieldId, value: string) => {
    const next = docs.map((d, i) => (i === item.index ? { ...d, [field]: value } : d));
    onDocsChange(next);
  };

  const scrollToSection = (id: string) => {
    const el = scrollRef.current?.querySelector(`[data-section-id="${id}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex h-full w-[min(100%,28rem)] shrink-0 min-w-0 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-3 text-xs text-slate-500">
        <div className="min-w-0 truncate">
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToOverview}
          >
            Brain
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToPillars}
          >
            Message pillars
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <span className="font-medium text-slate-800 line-clamp-1">{item.label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onDiscard}
            className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={onBackToPillars}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50">
          <MessageSquare className="h-4 w-4 text-blue-600" />
        </div>
        <div className="flex items-center gap-2">
          <Columns2 className="h-4 w-4 text-blue-600" />
          <h2 className="text-xl font-semibold text-slate-900">Message pillar</h2>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          One pain, your answer to it, and the proof.
        </p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-900"
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-6">
          <section data-section-id="pain" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">
              Pain <span className="font-normal text-rose-500">*</span>
            </label>
            <textarea
              value={item.doc.pain || ""}
              onChange={(e) => updateField("pain", e.target.value)}
              placeholder="The problem this pillar speaks to."
              rows={3}
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>

          <section data-section-id="valueProposition" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">
              Your answer <span className="font-normal text-rose-500">*</span>
            </label>
            <textarea
              value={item.doc.valueProposition || ""}
              onChange={(e) => updateField("valueProposition", e.target.value)}
              placeholder="How you solve that pain."
              rows={4}
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>

          <section data-section-id="proof" className="scroll-mt-4">
            <label className="block text-sm font-semibold text-slate-900">Proof</label>
            <textarea
              value={item.doc.proof || ""}
              onChange={(e) => updateField("proof", e.target.value)}
              placeholder="Evidence, metrics, or a customer story."
              rows={4}
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </section>
        </div>
      </div>
    </div>
  );
}
